<template>
    <v-dialog v-model="dialog" max-width="600px" lazy-validation persistent>
        <template v-slot:activator="{ on, attrs }">
            <slot name="activator" :on="on" :attrs="attrs">
                <v-btn color="primary" v-bind="attrs" v-on="on">
                    <v-icon left>mdi-plus</v-icon>
                    Variable
                </v-btn>
            </slot>
        </template>

        <v-card>
            <v-card-title class="flex-nowrap align-start">
                Add an environment variable
                <v-spacer></v-spacer>
                <v-btn @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-subtitle class="caption">
                This environment variable will be available in all applications of this {{ secretLevels[level].toLowerCase() }}.
            </v-card-subtitle>
            <v-card-text class="mb-0">
                <v-form v-model="envVarFormIsValid" @submit.prevent="sendPostRequest()">
                    <v-text-field v-model="newEnvVarName" outlined hint="This field is required" :rules="nameRules" dense autofocus>
                        <template v-slot:label>Type a name for your variable</template>
                    </v-text-field>
                    <v-textarea v-model="newEnvVarValue" outlined :rules="[rules.required]" dense hint="This field is required">
                        <template v-slot:label>Type a value for your variable</template>
                    </v-textarea>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn text color="primary" @click="sendPostRequest()" :disabled="!envVarFormIsValid" :loading="requestRunning">Add</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { enumsData } from '@/mixins/enums'
import eventBus from '@/components/EventBus'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as filesStorageErrors from '@/modules/files/errors'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as userErrors from '@/modules/user/errors/settings'

const { getBackendErrorMessage } = createBackendErrorHandler(filesStorageErrors, permissionsErrors, userErrors)

export default {
    name: 'EnvVarCreateDialog',
    mixins: [enumsData],
    props: {
        level: {
            type: Number,
            required: true
        },
        entityId: {
            type: [String, Number],
            default: null
        }
    },
    data() {
        return {
            dialog: false,
            requestRunning: false,
            envVarFormIsValid: false,
            newEnvVarName: '',
            newEnvVarValue: '',
            rules: {
                required: value => !!value || 'This field is required',
                validChars: p => /^[a-zA-Z0-9_-]*$/g.test(p) || 'Please use only letters, numbers, underscores and dashes.',
                validFirstChar: p => /^[a-zA-Z_]/.test(p) || 'Name can only start with a letter or an underscore.'
            }
        }
    },
    computed: {
        nameRules() {
            return [this.rules.required, this.rules.validFirstChar, this.rules.validChars]
        },
        paramForLevel() {
            const paramsForLevels = {
                0: 'personal',
                2: 'spaces',
                3: 'orgs'
            }
            return paramsForLevels[this.level]
        },
        resolvedEntityId() {
            if (this.entityId !== null && this.entityId !== undefined) {
                return this.entityId
            }
            if (this.level === 2) {
                return this.$route.params.sid
            }
            if (this.level === 3) {
                return this.$route.params.oid
            }
            return null
        }
    },
    methods: {
        closeDialog() {
            this.dialog = false
            this.newEnvVarName = ''
            this.newEnvVarValue = ''
        },
        async sendPostRequest() {
            if (!this.envVarFormIsValid) {
                return
            }

            const level = this.paramForLevel
            const id = this.resolvedEntityId
            if (level !== 'personal' && !id) {
                return
            }

            const endpoint = level === 'personal' ? '/env_vars/personal' : `/${level}/${id}/env_vars`

            this.requestRunning = true
            try {
                await this.$axios.post(endpoint, {
                    name: this.newEnvVarName,
                    value: this.newEnvVarValue
                })
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Successfully added environment variable.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
                this.$emit('created')
                eventBus.$emit('env-var-created', { level: this.level, entityId: id })
                this.closeDialog()
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, error.response?.data?.debug || 'There was an error saving the variable.')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
            this.requestRunning = false
        }
    }
}
</script>
